
import React from 'react';
import { KaosSettings, KaosTier, KaosAddon } from '../types';
import { Icon } from './Icon';

interface KaosAdminModuleProps {
  settings: KaosSettings;
  setSettings: React.Dispatch<React.SetStateAction<KaosSettings>>;
}

export const KaosAdminModule: React.FC<KaosAdminModuleProps> = ({ settings, setSettings }) => {
  const addTier = () => {
    const newTier: KaosTier = { id: 't-' + Date.now(), label: 'Paket Baru', price: 0 };
    setSettings({ ...settings, tiers: [...settings.tiers, newTier] });
  };

  const removeTier = (id: string) => {
    setSettings({ ...settings, tiers: settings.tiers.filter(t => t.id !== id) });
  };

  const updateTier = (id: string, field: keyof KaosTier, value: string | number) => {
    setSettings({
      ...settings,
      tiers: settings.tiers.map(t => t.id === id ? { ...t, [field]: value } : t)
    });
  };

  const addAddon = () => {
    const newAddon: KaosAddon = { id: 'a-' + Date.now(), label: 'Add-On Baru', price: 0 };
    setSettings({ ...settings, addons: [...settings.addons, newAddon] });
  };

  const removeAddon = (id: string) => {
    setSettings({ ...settings, addons: settings.addons.filter(a => a.id !== id) });
  };

  const updateAddon = (id: string, field: keyof KaosAddon, value: string | number) => {
    setSettings({
      ...settings,
      addons: settings.addons.map(a => a.id === id ? { ...a, [field]: value } : a)
    });
  };

  return (
    <div className="space-y-10 max-w-4xl pb-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-purple-500/20 rounded-lg">
          <Icon name="Shirt" className="text-purple-400" size={24} />
        </div>
        <h3 className="text-xl font-bold text-white">Manajemen Konveksi Kaos</h3>
      </div>

      {/* Base Pricing */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
          <label className="text-[10px] text-slate-500 font-bold uppercase tracking-widest block mb-2">Harga Combed 20S (Dasar)</label>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 font-bold text-sm">Rp</span>
            <input 
              type="number"
              className="bg-transparent text-xl font-bold text-white outline-none w-full focus:text-purple-400"
              value={settings.basePrice20S}
              onChange={(e) => setSettings({ ...settings, basePrice20S: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>
        <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
          <label className="text-[10px] text-slate-500 font-bold uppercase tracking-widest block mb-2">Harga Combed 24S (Dasar)</label>
          <div className="flex items-center gap-2">
            <span className="text-slate-400 font-bold text-sm">Rp</span>
            <input 
              type="number"
              className="bg-transparent text-xl font-bold text-white outline-none w-full focus:text-purple-400"
              value={settings.basePrice24S}
              onChange={(e) => setSettings({ ...settings, basePrice24S: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>
        <div className="bg-white/5 p-6 rounded-2xl border border-white/10">
          <label className="text-[10px] text-slate-500 font-bold uppercase tracking-widest block mb-2">Minimal Order (Pcs)</label>
          <input 
            type="number"
            className="bg-transparent text-xl font-bold text-white outline-none w-full focus:text-purple-400"
            value={settings.minOrder}
            onChange={(e) => setSettings({ ...settings, minOrder: parseInt(e.target.value) || 0 })}
          />
        </div>
      </div>

      {/* Tiers Management */}
      <div className="bg-white/5 p-8 rounded-3xl border border-white/10">
        <div className="flex justify-between items-center mb-6">
          <h4 className="text-lg font-bold text-white flex items-center gap-2">
             <Icon name="Zap" size={18} className="text-green-400" /> Daftar Harga Paket
          </h4>
          <button 
            onClick={addTier}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-xs font-bold text-white transition-all"
          >
            <Icon name="Plus" size={14} /> Tambah Paket
          </button>
        </div>
        <div className="space-y-4">
          {settings.tiers.map((tier) => (
            <div key={tier.id} className="flex gap-4 items-center bg-black/20 p-4 rounded-xl border border-white/5 group transition-all hover:border-white/10">
              <div className="flex-1">
                <label className="text-[10px] text-slate-500 font-bold uppercase block mb-1">Target Jumlah (Label)</label>
                <input 
                  className="w-full bg-transparent text-white outline-none font-medium border-b border-white/5 focus:border-purple-500"
                  value={tier.label}
                  onChange={(e) => updateTier(tier.id, 'label', e.target.value)}
                  placeholder="Contoh: Pesan 50 Pcs"
                />
              </div>
              <div className="w-48">
                <label className="text-[10px] text-slate-500 font-bold uppercase block mb-1">Harga / Pcs (Rp)</label>
                <input 
                  type="number"
                  className="w-full bg-transparent text-green-400 font-bold outline-none border-b border-white/5 focus:border-purple-500"
                  value={tier.price}
                  onChange={(e) => updateTier(tier.id, 'price', parseInt(e.target.value) || 0)}
                />
              </div>
              <button 
                onClick={() => removeTier(tier.id)}
                className="p-2 text-red-500/30 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
              >
                <Icon name="Trash2" size={18} />
              </button>
            </div>
          ))}
          {settings.tiers.length === 0 && <p className="text-center py-6 text-slate-600 italic text-sm">Belum ada paket ditambahkan.</p>}
        </div>
      </div>

      {/* Addons Management */}
      <div className="bg-white/5 p-8 rounded-3xl border border-white/10">
        <div className="flex justify-between items-center mb-6">
          <h4 className="text-lg font-bold text-white flex items-center gap-2">
            <Icon name="Scissors" size={18} className="text-yellow-400" /> Add-Ons & Custom
          </h4>
          <button 
            onClick={addAddon}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-xs font-bold text-white transition-all"
          >
            <Icon name="Plus" size={14} /> Tambah Add-on
          </button>
        </div>
        <div className="space-y-4">
          {settings.addons.map((addon) => (
            <div key={addon.id} className="flex gap-4 items-center bg-black/20 p-4 rounded-xl border border-white/5 group transition-all hover:border-white/10">
              <div className="flex-1">
                <label className="text-[10px] text-slate-500 font-bold uppercase block mb-1">Opsi Custom</label>
                <input 
                  className="w-full bg-transparent text-white outline-none font-medium border-b border-white/5 focus:border-purple-500"
                  value={addon.label}
                  onChange={(e) => updateAddon(addon.id, 'label', e.target.value)}
                  placeholder="Contoh: Model Tunik"
                />
              </div>
              <div className="w-48">
                <label className="text-[10px] text-slate-500 font-bold uppercase block mb-1">Biaya Tambah (Rp)</label>
                <input 
                  type="number"
                  className="w-full bg-transparent text-yellow-400 font-bold outline-none border-b border-white/5 focus:border-purple-500"
                  value={addon.price}
                  onChange={(e) => updateAddon(addon.id, 'price', parseInt(e.target.value) || 0)}
                />
              </div>
              <button 
                onClick={() => removeAddon(addon.id)}
                className="p-2 text-red-500/30 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
              >
                <Icon name="Trash2" size={18} />
              </button>
            </div>
          ))}
          {settings.addons.length === 0 && <p className="text-center py-6 text-slate-600 italic text-sm">Belum ada add-on ditambahkan.</p>}
        </div>
      </div>
    </div>
  );
};
